import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CENTaxTag from "@tCentro/Icons/CENTaxTag";
import React from "react";
import { css } from "styled-components";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    return {
        siteName: state.context.website.name,
    };
};

interface CENTaxExProps {
    siteName: string;
}

export interface CENTaxExStyles {
    mainContainer: InjectableCss;
    iconStyle: IconPresentationProps;
    iconWrapper: InjectableCss;
    taxLineFirst: InjectableCss;
    taxLineSecond: InjectableCss;
}

export const cenTaxExStyles: CENTaxExStyles = {
    mainContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 8px;
            border: 2px solid #f2f2f2;
            padding: 16px;
            // margin-top: 48px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 14px;
                    `,
                    css`
                        padding: 14px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    iconStyle: {
        size: 28,
        css: css`
            svg {
                > path {
                    fill: ${getColor("text.link")};
                }
            }
            width: 40px;
            height: 40px;
            justify-content: center;
            align-items: center;
        `,
    },
    iconWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },
    taxLineFirst: {
        css: css`
            display: block;
            font-weight: 600;
            font-size: 18px;
            line-height: 24px;
            color: ${getColor("text.link")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 16px;
                    `,
                    css`
                        font-size: 16px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    taxLineSecond: {
        css: css`
            display: block;
            font-size: 14px;
            font-weight: 600;
            line-height: 20px;
            color: #636161;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 11px;
                    `,
                    css`
                        font-size: 11px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};
const styles = cenTaxExStyles;
const CENTaxEx: React.FC<CENTaxExProps> = ({ siteName }) => {
    const salesSupportEmailId = siteName === "Nisco" ? "admin@niscoinc.com" : "onlinesalessupport@centrosolves.com";
    const mobileNumber = siteName === "Nisco" ? "(901) 883-7241" : "(800) 238-6672";
    const telMobileNumber = siteName === "Nisco" ? "19018837241" : "18002386672";
    return (
        <StyledWrapper
            {...styles.mainContainer}
            style={{
                marginTop: siteName === "Nisco" ? "0px" : "48px",
            }}
        >
            <StyledWrapper {...styles.iconWrapper}>
                <Icon src={CENTaxTag} {...styles.iconStyle} />
            </StyledWrapper>
            <StyledWrapper>
                <Typography {...styles.taxLineFirst}>{translate("Tax Exempt Customers")}</Typography>
                {siteName !== "Nisco" ? (
                    <Typography {...styles.taxLineSecond}>
                        {translate("Email your certificate to")}&nbsp;
                        <Link href={`mailto:${salesSupportEmailId}`}>{salesSupportEmailId}</Link>
                        &nbsp;{translate("or contact us at")}&nbsp;
                        <Link href={`tel:${telMobileNumber}`}>{mobileNumber}</Link>
                        &nbsp;{translate("for fast, one-time exemption setup.")}
                    </Typography>
                ) : (
                    <>
                        <Typography style={{ marginTop: "8px" }} as="p" {...styles.taxLineSecond}>
                            {translate("To request tax-exempt status, please")}&nbsp;
                            <Link href="/MyAccount/CreateAccount">{translate("register for an account")}</Link>
                            &nbsp;
                            {translate("and upload your tax exemption certificate during the registration process.")}
                        </Typography>
                        <Typography style={{ marginTop: "8px" }} as="p" {...styles.taxLineSecond}>
                            {translate(
                                "Our team will review your information and contact you shortly to complete the setup.",
                            )}
                        </Typography>
                    </>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CENTaxEx),
    definition: {
        group: "Contact Us",
        displayName: "CENTaxEx",
    },
};

export default widgetModule;
